/**
 * 用途：授权账号管理页，管理已通过 OAuth 授权接入的广告平台账号，支持新增授权和解绑
 * 路由：/auth-accounts
 */
import { useEffect, useState } from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import Pagination from '../components/Pagination';
import { confirmDialog } from '../store/useConfirmStore';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authAccountsApi } from '../api/authAccounts';
import type { PlatformAuthAccount } from '../api/authAccounts';
import { accountsApi } from '../api/accounts';
import { useAppStore } from '../store/useAppStore';
import { useToastStore } from '../store/useToastStore';
import { TableLoadingCell, TableLoadingOverlay } from '../components/TableLoadingOverlay';

type PlatformFilter = 'all' | 'facebook' | 'google' | 'tiktok';

const DEFAULT_PAGE_SIZE = 10;

function formatDateTime(input?: string): string {
  if (!input) return '-';
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString();
}

function getStatusLabel(status: string): string {
  switch (status) {
    case 'active': return '已授权';
    case 'expired': return '已过期';
    case 'inactive': return '未启用';
    case 'error': return '异常';
    case 'pending': return '待授权';
    default: return status;
  }
}

function statusClass(status: string): string {
  switch (status) {
    case 'active': return 'status-active';
    case 'expired':
    case 'error': return 'status-error';
    default: return 'status-paused';
  }
}

export default function AuthAccounts() {
  const queryClient = useQueryClient();
  const { brandId, siteId, hasPermission, adContext } = useAppStore();
  const userLevel = adContext?.data_level ?? 1;
  const canManageAuthAccounts = userLevel >= 3;
  const showToast = useToastStore((s) => s.show);
  const isMobile = useIsMobile();

  const [platformFilter, setPlatformFilter] = useState<PlatformFilter>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [refreshingID, setRefreshingID] = useState<string | null>(null);
  const [bindPlatform, setBindPlatform] = useState<'facebook' | 'google' | 'tiktok'>('facebook');
  const [bindRedirecting, setBindRedirecting] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const listQuery = useQuery({
    queryKey: ['authAccounts', platformFilter, currentPage, pageSize],
    queryFn: () =>
      authAccountsApi.list({
        page: currentPage,
        size: pageSize,
        platform: platformFilter === 'all' ? undefined : platformFilter,
      }),
    refetchInterval: 30000,
  });

  const bindMutation = useMutation({
    onMutate: () => { setBindRedirecting(true); },
    mutationFn: () =>
      accountsApi.bind({
        brand_id: brandId,
        site_id: siteId,
        platform: bindPlatform,
        redirect_uri: `${window.location.origin}/oauth/callback`,
      }),
    onSuccess: (res) => { window.location.href = res.oauth_url; },
    onError: (err: Error) => {
      setBindRedirecting(false);
      setToast({ type: 'error', message: err.message || '发起授权失败' });
    },
  });

  const refreshMutation = useMutation({
    mutationFn: async (id: string) => {
      setRefreshingID(id);
      return authAccountsApi.refresh(id);
    },
    onSuccess: (res) => {
      if (!res.success) {
        setToast({ type: 'error', message: res.error || 'Token 刷新失败' });
        return;
      }
      setToast({ type: 'success', message: 'Token 刷新成功' });
      void queryClient.invalidateQueries({ queryKey: ['authAccounts'] });
    },
    onError: (err: Error) => {
      setToast({ type: 'error', message: err.message || 'Token 刷新失败' });
    },
    onSettled: () => { setRefreshingID(null); },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => authAccountsApi.delete(id),
    onSuccess: () => {
      setToast({ type: 'success', message: '删除成功' });
      void queryClient.invalidateQueries({ queryKey: ['authAccounts'] });
    },
    onError: (err: Error) => {
      setToast({ type: 'error', message: err.message || '删除失败' });
    },
  });

  const accounts = listQuery.data?.data ?? [];
  const total = listQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const showLeaderPermissionToast = () => showToast('需主管及以上权限操作授权账号');

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 4000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  return (
    <div className="page-section" style={{ display: 'block' }}>
      {toast && (
        <div
          style={{
            position: 'fixed', top: 16, right: 16, padding: '12px 16px', borderRadius: 'var(--radius)',
            color: '#fff', fontSize: 13, zIndex: 9999,
            background: toast.type === 'success' ? 'var(--green)' : 'var(--red)',
            maxWidth: 360, display: 'flex', alignItems: 'flex-start', gap: 12,
            boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
          }}
        >
          <span style={{ flex: 1, lineHeight: 1.5, wordBreak: 'break-word' }}>{toast.message}</span>
          <button type="button" onClick={() => setToast(null)}
            style={{ border: 'none', background: 'transparent', color: '#fff', cursor: 'pointer', padding: 0, fontSize: 16, lineHeight: 1 }}
            aria-label="关闭提示">x</button>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: isMobile ? 12 : 0 }}>
        <div>
          <div className="section-title">授权管理</div>
          <div className="section-desc" style={{ marginTop: 4 }}>
            管理 Facebook / Google / TikTok 平台 OAuth 授权，刷新 Token。
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ display: 'flex', gap: 6, background: 'var(--surface2)', borderRadius: 8, padding: 4 }}>
            {(['facebook', 'google', 'tiktok'] as const).map((p) => (
              <button key={p} className="btn btn-ghost btn-sm"
                style={{ opacity: bindPlatform === p ? 1 : 0.7 }}
                onClick={() => setBindPlatform(p)}
                disabled={bindMutation.isPending || bindRedirecting}>
                {p === 'facebook' ? 'Facebook' : p === 'google' ? 'Google' : 'TikTok'}
              </button>
            ))}
          </div>
          <button className="btn btn-primary"
            onClick={() => {
              if (bindMutation.isPending || bindRedirecting) return;
              if (!hasPermission('system:accounts:bind')) {
                showToast('暂无绑定账号权限');
                return;
              }
              bindMutation.mutate();
            }}
            disabled={bindMutation.isPending || bindRedirecting}>
            {bindMutation.isPending || bindRedirecting ? '跳转授权中...' : `绑定 ${bindPlatform === 'facebook' ? 'Facebook' : bindPlatform === 'tiktok' ? 'TikTok' : 'Google'}`}
          </button>
        </div>
      </div>

      <div className="toolbar">
        <div className="page-tabs">
          {(['all', 'facebook', 'google', 'tiktok'] as PlatformFilter[]).map((f) => (
            <div key={f} className={`page-tab ${platformFilter === f ? 'active' : ''}`}
              onClick={() => { setPlatformFilter(f); setCurrentPage(1); }}>
              {f === 'all' ? '全部' : f === 'facebook' ? 'Facebook' : f === 'google' ? 'Google' : 'TikTok'}
            </div>
          ))}
        </div>
      </div>

      <div className="card" style={{ padding: 0, marginTop: 16 }}>
        <div className="table-wrap" style={{ position: 'relative' }}>
          <table style={{ minWidth: 600 }}>
            <thead>
              <tr>
                <th style={{ whiteSpace: 'nowrap' }}>平台</th>
                <th style={{ whiteSpace: 'nowrap' }}>账号名称</th>
                <th style={{ whiteSpace: 'nowrap' }}>账号 ID</th>
                <th style={{ whiteSpace: 'nowrap' }}>状态</th>
                <th style={{ whiteSpace: 'nowrap' }}>Token 到期</th>
                <th style={{ whiteSpace: 'nowrap' }}>创建时间</th>
                <th style={{ whiteSpace: 'nowrap' }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {listQuery.isLoading && accounts.length === 0 && (
                <tr><td colSpan={7}><TableLoadingCell /></td></tr>
              )}
              {listQuery.isError && (
                <tr><td colSpan={7} style={{ padding: 24, color: 'var(--red)', textAlign: 'center' }}>
                  {(listQuery.error as Error).message || '加载失败'}
                </td></tr>
              )}
              {!listQuery.isLoading && !listQuery.isError && accounts.length === 0 && (
                <tr><td colSpan={7} style={{ padding: 24, color: 'var(--text3)', textAlign: 'center' }}>
                  暂无授权账号，点击右上角进行 OAuth 绑定。
                </td></tr>
              )}
              {accounts.map((a: PlatformAuthAccount) => (
                <tr key={a.id}>
                  <td style={{ textTransform: 'capitalize' }}>{a.platform}</td>
                  <td>{a.account_name || '-'}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{a.account_id}</td>
                  <td><span className={`status ${statusClass(a.status)}`}>{getStatusLabel(a.status)}</span></td>
                  <td>{formatDateTime(a.token_expiry)}</td>
                  <td>{formatDateTime(a.created_at)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn btn-ghost btn-sm"
                        onClick={() => {
                          if (!canManageAuthAccounts) { showLeaderPermissionToast(); return; }
                          refreshMutation.mutate(a.id);
                        }}
                        disabled={refreshingID === a.id || !canManageAuthAccounts}>
                        {refreshingID === a.id ? '刷新中...' : '刷新 Token'}
                      </button>
                      <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)' }}
                        onClick={async () => {
                          if (!canManageAuthAccounts) { showLeaderPermissionToast(); return; }
                          const ok = await confirmDialog({
                            title: '确认删除',
                            message: `确定要删除授权账号 ${a.account_name || a.account_id} 吗？`,
                          });
                          if (ok) deleteMutation.mutate(a.id);
                        }}
                        disabled={!canManageAuthAccounts || deleteMutation.isPending}>
                        删除
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {listQuery.isFetching && accounts.length > 0 && <TableLoadingOverlay />}
        </div>

        <Pagination
          page={currentPage}
          totalPages={totalPages}
          onChange={(p) => setCurrentPage(p)}
          total={`共 ${total} 条`}
          pageSize={pageSize}
          pageSizeOptions={[10, 20, 50, 100]}
          onPageSizeChange={nextPageSize => { setCurrentPage(1); setPageSize(nextPageSize); }}
          disabled={listQuery.isFetching}
        />
      </div>
    </div>
  );
}
